/**
 * The OS composer in live mode (docs/plans/os-live-mode.md, Decided 16, step
 * 4.1; the boards "Talk live on the page", "Live mode, turn by turn" and "How
 * the live button moves"; parts.mjs `osComposer` live, `osLiveButton`): what
 * the box holds while setup's live call is on, in place of the words and the
 * row. OS's own live mode is the kit's long pill (`components/kit/live-pill.tsx`,
 * docs/plans/os-voice-conversation.md step 3.9); setup moves to it in 5.4,
 * and this goes with it.
 *
 *   top bar   Sound, Mute and the timer on the left; Type instead and Stop
 *             live on the right (a flow's own last step takes Stop live's
 *             place: `end`)
 *   middle    the person's own words, centred, only while they speak. What OS
 *             says is in the conversation, never here.
 *   foot      the live button: it says the state and is the mic. A click talks
 *             in every state.
 *
 * Every prop is data or a callback; the box (`OsComposer`) and the call
 * (`useCoachVoice`) are the owner's. The timer ticks on its own, so a second
 * re-renders nothing else.
 *
 * `DictationNotice` is the line over the box when the last dictation did not
 * come through. Dictation itself is written live in the box
 * (`features/coach/os-composer`).
 */
import { t } from '@alluzo/shared';
import { Keyboard, Mic, MicOff, VolumeX } from 'lucide-react';
import { useEffect, useState, type ReactElement, type ReactNode } from 'react';

import { Button } from '~/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { cn } from '~/lib/utils';

import { clockOf, liveButtonOf, type LiveTurn } from '../live-mode';
import type { DictationStatus } from '../use-dictation';

/* ------------------------------------------------------------ live mode */

export type LiveComposerProps = {
  /** Where live mode is: coming up, or whose turn it is. */
  readonly state: LiveTurn | 'connecting';
  /** The person's own words while they speak; null between turns. */
  readonly words: string | null;
  /** A line under the live button, when one says more than it: "Still listening, in case you add something". */
  readonly hint: string | null;
  /** When live mode began (ms since the epoch), for the timer. */
  readonly startedAt: number | null;
  /** The full page: the live button is 360 in the centre, and the words are 16 px. */
  readonly page: boolean;
  /** Going back to chat: the button shrinks back into its slot (Type instead, Stop live). */
  readonly leaving?: boolean;
  /** A flow's own last step, in Stop live's place. */
  readonly end?: ReactNode;
  readonly onMute: () => void;
  readonly onType: () => void;
  readonly onStop: () => void;
  readonly onTalk: () => void;
};

export function LiveComposer({
  state,
  words,
  hint,
  startedAt,
  page,
  leaving = false,
  end,
  onMute,
  onType,
  onStop,
  onTalk,
}: LiveComposerProps): ReactElement {
  const muted = state === 'muted';
  const small = !page;
  const shown = state === 'listening' && words !== null && words.trim() !== '';
  return (
    <div className={cn('flex flex-col', page ? 'gap-4' : 'gap-3')} data-slot="os-live" data-leaving={leaving ? '' : undefined}>
      <div className="flex min-w-0 items-center gap-1.5" data-slot="os-live-bar">
        <BarButton label={muted ? t('agent.live.unmute') : t('agent.live.mute')} filled={muted} pressed={muted} small={small} onPress={onMute}>
          {muted ? <MicOff aria-hidden /> : <Mic aria-hidden />}
        </BarButton>
        <LiveClock startedAt={startedAt} />
        <span className="flex-1" />
        <BarButton label={t('agent.voice.type_instead')} outline small={small} onPress={onType}>
          <Keyboard aria-hidden />
        </BarButton>
        {end ?? (
          <Button type="button" variant="outline" size={small ? 'sm' : 'default'} className="rounded-max font-semibold" onClick={onStop}>
            {t('agent.live.end')}
          </Button>
        )}
      </div>
      {shown ? (
        // Words still arriving are not read out piece by piece; the button says the state.
        <p className={cn('line-clamp-2 px-0.5 text-center text-foreground', page ? 'text-lg leading-6' : 'text-base leading-5')} aria-live="off" data-slot="os-live-words">
          {words}
        </p>
      ) : null}
      <div className="flex flex-col items-center gap-2">
        <LiveButton state={state} sound page={page} onPress={onTalk} />
        {hint === null ? null : (
          <p className="text-center text-sm text-muted-foreground" role="status">
            {hint}
          </p>
        )}
      </div>
    </div>
  );
}

/** One of the top bar's round icon buttons, named on hover. Sound off and Mute are filled black; on, plain. */
function BarButton({
  label,
  filled = false,
  outline = false,
  pressed,
  small,
  onPress,
  children,
}: {
  readonly label: string;
  readonly filled?: boolean;
  readonly outline?: boolean;
  readonly pressed?: boolean;
  readonly small: boolean;
  readonly onPress: () => void;
  readonly children: ReactElement;
}): ReactElement {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant={filled ? 'default' : outline ? 'outline' : 'ghost'}
          size={small ? 'icon-sm' : 'icon'}
          aria-label={label}
          aria-pressed={pressed}
          className="rounded-max"
          onClick={onPress}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/** The time live mode has run, ticking on its own. */
function LiveClock({ startedAt }: { readonly startedAt: number | null }): ReactElement | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (startedAt === null) return undefined;
    const tick = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(tick);
  }, [startedAt]);
  if (startedAt === null) return null;
  return <span className="shrink-0 px-1 font-mono text-sm text-muted-foreground tabular-nums">{clockOf(now - startedAt)}</span>;
}

/**
 * The live button (Idea F): one wide button that says the state and is the
 * mic. Its motion is CSS (`globals.css`, `[data-slot="os-live-button"]`), in
 * OS's two blues: lines run in while it listens, out while OS speaks, a light
 * turns round it while OS works or it connects; still and black on the
 * person's move, still and grey when they muted it.
 */
export function LiveButton({
  state,
  sound,
  page,
  onPress,
}: {
  readonly state: LiveTurn | 'connecting';
  readonly sound: boolean;
  readonly page: boolean;
  readonly onPress: () => void;
}): ReactElement {
  const button = liveButtonOf(state, sound);
  const rippling = button.look === 'listening' || button.look === 'speaking';
  return (
    <button
      type="button"
      data-slot="os-live-button"
      data-look={button.look}
      data-size={page ? 'page' : 'small'}
      aria-label={button.label}
      // Nothing to talk to until the call is up.
      disabled={state === 'connecting'}
      onClick={onPress}
    >
      {rippling ? (
        <>
          <span data-rip="1" aria-hidden />
          <span data-rip="2" aria-hidden />
          <span data-rip="3" aria-hidden />
        </>
      ) : null}
      {button.glyph === 'mic_off' ? <MicOff aria-hidden /> : button.glyph === 'sound_off' ? <VolumeX aria-hidden /> : <Mic aria-hidden />}
      <span data-slot="os-live-button-label">{button.label}</span>
    </button>
  );
}

/* ----------------------------------------------------------- the dictation */

/**
 * What went wrong with the last dictation, over the box until the next one or
 * a cancel. A browser's refused microphone is a setting of the site, which a
 * page cannot open, so the line says where it is instead of offering a button.
 */
export function DictationNotice({ status, className }: { readonly status: DictationStatus; readonly className?: string }): ReactElement | null {
  const words =
    status === 'failed'
      ? t('agent.dictation.failed')
      : status === 'mic_off'
        ? t('agent.web.dictation_mic_off')
        : status === 'no_mic'
          ? t('agent.web.dictation_no_microphone')
          : null;
  if (words === null) return null;
  return (
    <p className={cn('text-sm text-muted-foreground', className)} role="status">
      {words}
    </p>
  );
}
